import React, { useState, useEffect, useCallback } from "react";
import ApiClient from "../api/ApiClient";
import { useAuth } from "../contexts/AuthContext";
import SignalFile from "../entities/SignalFile";

const pageSizeOptions = [10, 25, 50, 100];

// Reusable formatting for SignalFile timestamps. The API layer
// (ApiClient) wraps per-row payloads as `SignalFile` entities, so any
// caller that already holds an entity can call `getFormattedCreateTime()`
// directly. The modal's top-level payload is a plain object (it carries
// calculation-level metadata, not per-file state), so this helper
// wraps it in a SignalFile first so the formatter is the single
// source of truth.
function renderCreateTime(value) {
  if (!value) return "—";
  const entity = typeof value.getFormattedCreateTime === "function"
    ? value
    : new SignalFile({ CreateTime: value.CreateTime });
  return entity.getFormattedCreateTime() || "—";
}

function SignalActionBadge({ action }) {
  const colors = {
    Buy: "success",
    Sell: "danger",
    Money: "secondary",
  };
  return (
    <span className={`badge bg-${colors[action] || "dark"} me-1`}>{action}</span>
  );
}

function SignalTable({ file, order = "asc" }) {
  // Render the per-file "Show Signals" expansion. We call the
  // SignalFile entity's formatter once on the parent file, then read
  // each formatted signal's `FormattedDate` directly — no per-row
  // wrapper, no ad-hoc moment calls.
  const formattedSignals = file?.getFormattedSignals?.() || [];

  if (formattedSignals.length === 0) {
    return <p className="text-muted small mb-0">No signals</p>;
  }

  const sorted = [...formattedSignals].sort((a, b) => {
    const diff = new Date(a.Time) - new Date(b.Time);
    return order === "asc" ? diff : -diff;
  });

  return (
    <div style={{ maxHeight: 300, overflowY: "auto" }}>
      <table className="table table-sm table-striped mb-0">
        <thead className="table-light sticky-top">
          <tr>
            <th>Date</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((s, i) => (
            <tr key={i}>
              <td>{s.FormattedDate}</td>
              <td>
                <SignalActionBadge action={s.Action} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CorrelationModal({ calculationId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [expandedFileId, setExpandedFileId] = useState(null);
  const [viewMode, setViewMode] = useState("accordion"); // "accordion" | "sidebyside"
  const [dateOrder, setDateOrder] = useState("desc"); // "asc" | "desc"

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const result = await ApiClient.getSignalFilesByCorrelationId(calculationId);
        if (!cancelled) setData(result);
      } catch (err) {
        if (!cancelled) setError(err.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [calculationId]);

  function toggleFile(id) {
    setExpandedFileId((prev) => (prev === id ? null : id));
  }

  function toggleDateOrder() {
    setDateOrder((prev) => (prev === "asc" ? "desc" : "asc"));
  }

  return (
    <div
      className="modal show d-block"
      tabIndex="-1"
      style={{ background: "rgba(0,0,0,0.5)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              Signal Files — Calculation ID:{" "}
              <code className="ms-1">{calculationId}</code>
            </h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            {loading && (
              <div className="text-center py-4">
                <div className="spinner-border" role="status" />
              </div>
            )}
            {error && (
              <div className="alert alert-danger">{error}</div>
            )}
            {data && (
              <>
                <dl className="row mb-3">
                  <dt className="col-sm-3">Model</dt>
                  <dd className="col-sm-9">{data.ModelName || data.ModelFileName || `#${data.ModelId}`}</dd>
                  <dt className="col-sm-3">Created</dt>
                  <dd className="col-sm-9">{renderCreateTime(data)}</dd>
                  <dt className="col-sm-3">Files</dt>
                  <dd className="col-sm-9">{data.Files?.length ?? 0}</dd>
                  <dt className="col-sm-3">Combine mode</dt>
                  <dd className="col-sm-9">
                    <span className="badge bg-secondary">{data.ModelSignalCombineMode || 'CbSoM'}</span>
                    <span className="ms-2 small text-muted">
                      Combined signal is calculated server-side using the mode stored on the model.
                    </span>
                  </dd>
                </dl>
                <div className="d-flex gap-2 mb-3 align-items-center">
                  <button
                    className={`btn btn-sm ${viewMode === "accordion" ? "btn-primary" : "btn-outline-secondary"}`}
                    onClick={() => setViewMode("accordion")}
                  >
                    Per File
                  </button>
                  <button
                    className={`btn btn-sm ${viewMode === "sidebyside" ? "btn-primary" : "btn-outline-secondary"}`}
                    onClick={() => setViewMode("sidebyside")}
                  >
                    Side-by-Side
                  </button>
                  <div className="ms-auto">
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={toggleDateOrder}
                      title="Toggle date order"
                    >
                      Date {dateOrder === "asc" ? "↑ ASC" : "↓ DESC"}
                    </button>
                  </div>
                </div>
                {viewMode === "sidebyside" ? (
                  <SideBySideTable files={data.Files || []} order={dateOrder} />
                ) : (
                <div className="accordion" id="correlationFilesAccordion">
                  {(data.Files || []).map((file) => {
                    const isExpanded = expandedFileId === file.Id;
                    const signalCounts = countActions(file.Signals);
                    return (
                      <div className="accordion-item" key={file.Id}>
                        <h2 className="accordion-header">
                          <button
                            className={`accordion-button ${isExpanded ? "" : "collapsed"}`}
                            type="button"
                            onClick={() => toggleFile(file.Id)}
                          >
                            <span className="me-2">
                              <strong>#{file.Id}</strong>
                            </span>
                            {file.Tag && (
                              <span className="badge bg-info text-dark me-2">{file.Tag}</span>
                            )}
                            {file.RealtimeCandlesUsed && (
                              <span className="badge bg-warning text-dark me-2" title="Calculated with real-time candles">RT Candles</span>
                            )}
                            <span className="text-muted small me-3">
                              {renderCreateTime(file)}
                            </span>
                            <span className="ms-auto me-3">
                              {signalCounts.Buy > 0 && (
                                <span className="badge bg-success me-1">Buy: {signalCounts.Buy}</span>
                              )}
                              {signalCounts.Sell > 0 && (
                                <span className="badge bg-danger me-1">Sell: {signalCounts.Sell}</span>
                              )}
                              {signalCounts.Money > 0 && (
                                <span className="badge bg-secondary me-1">Money: {signalCounts.Money}</span>
                              )}
                              <span className="badge bg-light text-dark border">
                                Total: {file.Signals?.length ?? 0}
                              </span>
                            </span>
                          </button>
                        </h2>
                        {isExpanded && (
                          <div className="accordion-collapse">
                            <div className="accordion-body">
                              <SignalTable file={file} order={dateOrder} />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                )}
              </>
            )}
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function countActions(signals) {
  const counts = { Buy: 0, Sell: 0, Money: 0 };
  if (!signals) return counts;
  signals.forEach((s) => {
    if (counts.hasOwnProperty(s.Action)) counts[s.Action]++;
  });
  return counts;
}

// Normalise a signal Time value to a plain UTC date string "YYYY-MM-DD"
// for grouping. Routed through the SignalFile entity so the key
// format and timezone stay aligned with `FormattedDate` — both flow
// from `moment.utc(...)` of the same input string.
function toDateKey(timeStr) {
  return SignalFile.getSignalDateKey(timeStr);
}

function formatDateKey(key) {
  if (!key) return "—";
  // key is already YYYY-MM-DD (UTC); format as MM/DD/YYYY for display
  const [y, m, d] = key.split("-");
  return `${m}/${d}/${y}`;
}

function SideBySideTable({ files, order = "asc" }) {
  if (!files || files.length === 0) {
    return <p className="text-muted small">No files</p>;
  }

  // Collect all unique dates across all files, sorted per order
  const dateSet = new Set();
  files.forEach((file) => {
    (file.Signals || []).forEach((s) => dateSet.add(toDateKey(s.Time)));
  });
  const dates = Array.from(dateSet).sort((a, b) =>
    order === "asc" ? a.localeCompare(b) : b.localeCompare(a)
  );

  // Build per-file lookup: dateKey -> action
  const fileMaps = files.map((file) => {
    const map = {};
    (file.Signals || []).forEach((s) => {
      const key = toDateKey(s.Time);
      // If multiple signals on same date keep last (or first — just pick one)
      map[key] = s.Action;
    });
    return map;
  });

  const actionColors = { Buy: "success", Sell: "danger", Money: "secondary" };

  return (
    <div style={{ overflowX: "auto", maxHeight: 420, overflowY: "auto" }}>
      <table className="table table-sm table-bordered table-striped mb-0" style={{ minWidth: 200 + files.length * 110 }}>
        <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
          <tr>
            <th style={{ minWidth: 100 }}>Date</th>
            {files.map((file) => (
              <th key={file.Id} style={{ minWidth: 110, textAlign: "center" }}>
                {file.Tag
                  ? <span className="badge bg-info text-dark">{file.Tag}</span>
                  : <span className="text-muted small">#{file.Id}</span>}
                {file.RealtimeCandlesUsed && (
                  <span className="badge bg-warning text-dark d-block mt-1" title="Calculated with real-time candles">RT Candles</span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {dates.length === 0 && (
            <tr>
              <td colSpan={files.length + 1} className="text-center text-muted">No signals</td>
            </tr>
          )}
          {dates.map((dateKey) => (
            <tr key={dateKey}>
              <td className="small text-nowrap">{formatDateKey(dateKey)}</td>
              {fileMaps.map((map, i) => {
                const action = map[dateKey];
                return (
                  <td key={i} style={{ textAlign: "center" }}>
                    {action
                      ? <span className={`badge bg-${actionColors[action] || "dark"}`}>{action}</span>
                      : <span className="text-muted">—</span>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export const SignalFiles = () => {
  const { user, isAuthenticated } = useAuth();
  const isAdmin = isAuthenticated && user?.Roles?.includes('Admin');

  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(0);

  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deletingCalcId, setDeletingCalcId] = useState(null);

  const [selectedCalculationId, setSelectedCalculationId] = useState(null);
  const [notification, setNotification] = useState(null); // { type: 'success'|'danger', message }
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const body = {
        Limit: pageSize,
        Offset: page * pageSize,
      };
      const data = await ApiClient.searchSignalFilesByCalculationId(body);
      setItems(data.Items || []);
      setTotal(data.TotalCount || 0);
    } catch (err) {
      console.error("Failed to fetch signal files by calculation ID", err);
      setError(err.message || "Failed to load data");
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const totalPages = Math.ceil(total / pageSize) || 1;

  function handlePageChange(newPage) {
    if (newPage < 0 || newPage >= totalPages) return;
    setPage(newPage);
  }

  async function confirmAndDelete(calculationId) {
    setConfirmDeleteId(calculationId);
  }

  async function deleteByCalculationId(calculationId) {
    setConfirmDeleteId(null);
    setDeletingCalcId(calculationId);
    setNotification(null);
    try {
      const result = await ApiClient.deleteSignalFilesByCalculationId(calculationId);
      setItems((prev) => prev.filter((item) => item.CalculationId !== calculationId));
      setTotal((prev) => Math.max(0, prev - 1));
      setNotification({ type: 'success', message: `Deleted ${result.Deleted} signal file(s) for calculation ${calculationId}.` });
    } catch (err) {
      console.error('Delete by calculation ID failed', err);
      setNotification({ type: 'danger', message: 'Delete failed: ' + (err.message || 'Unknown error') });
    } finally {
      setDeletingCalcId(null);
    }
  }

  return (
    <div className="container-fluid mt-3">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h2 className="mb-0">Signal Files</h2>
        <div className="d-flex align-items-center gap-2">
          <label className="mb-0 me-1 small text-muted">Page size:</label>
          <select
            className="form-select form-select-sm"
            style={{ width: "auto" }}
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
          >
            {pageSizeOptions.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {notification && (
        <div className={`alert alert-${notification.type} alert-dismissible mb-3`} role="alert">
          {notification.message}
          <button type="button" className="btn-close" onClick={() => setNotification(null)} />
        </div>
      )}

      {confirmDeleteId && (
        <div className="alert alert-warning d-flex align-items-center justify-content-between mb-3" role="alert">
          <span>
            Delete all signal files for calculation <code>{confirmDeleteId}</code>?
          </span>
          <div className="d-flex gap-2 ms-3">
            <button
              className="btn btn-sm btn-danger"
              onClick={() => deleteByCalculationId(confirmDeleteId)}
            >
              Yes, delete
            </button>
            <button
              className="btn btn-sm btn-secondary"
              onClick={() => setConfirmDeleteId(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" role="status" />
        </div>
      ) : (
        <>
          <div className="table-responsive">
            <table className="table table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th>Calculation ID</th>
                  <th>Model</th>
                  <th>Tags</th>
                  <th>Files</th>
                  <th>Created</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center text-muted py-4">
                      No signal files found
                    </td>
                  </tr>
                )}
                {items.map((item) => (
                  <tr key={item.CalculationId}>
                    <td>
                      <code className="small">{item.CalculationId}</code>
                    </td>
                    <td>{item.ModelName || item.ModelFileName || `#${item.ModelId}`}</td>
                    <td>
                      {(item.Tags || []).map((tag) => (
                        <span key={tag} className="badge bg-info text-dark me-1">
                          {tag}
                        </span>
                      ))}
                      {(!item.Tags || item.Tags.length === 0) && (
                        <span className="text-muted small">—</span>
                      )}
                    </td>
                    <td>
                      <span className="badge bg-primary">{item.FileCount}</span>
                    </td>
                    <td className="small text-muted">{renderCreateTime(item)}</td>
                    <td>
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => setSelectedCalculationId(item.CalculationId)}
                        >
                          View
                        </button>
                        {isAdmin && (
                          <button
                            className="btn btn-sm btn-outline-danger"
                            disabled={deletingCalcId === item.CalculationId}
                            onClick={() => confirmAndDelete(item.CalculationId)}
                            title="Delete all signal files for this calculation"
                          >
                            {deletingCalcId === item.CalculationId
                              ? <span className="spinner-border spinner-border-sm" />
                              : 'Delete'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="d-flex align-items-center justify-content-between mt-2">
            <small className="text-muted">
              {total > 0
                ? `Showing ${page * pageSize + 1}–${Math.min((page + 1) * pageSize, total)} of ${total}`
                : "No results"}
            </small>
            <nav>
              <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${page === 0 ? "disabled" : ""}`}>
                  <button className="page-link" onClick={() => handlePageChange(page - 1)}>
                    &laquo;
                  </button>
                </li>
                {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 7) {
                    pageNum = i;
                  } else if (page < 4) {
                    pageNum = i;
                  } else if (page > totalPages - 4) {
                    pageNum = totalPages - 7 + i;
                  } else {
                    pageNum = page - 3 + i;
                  }
                  return (
                    <li
                      key={pageNum}
                      className={`page-item ${page === pageNum ? "active" : ""}`}
                    >
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(pageNum)}
                      >
                        {pageNum + 1}
                      </button>
                    </li>
                  );
                })}
                <li className={`page-item ${page >= totalPages - 1 ? "disabled" : ""}`}>
                  <button className="page-link" onClick={() => handlePageChange(page + 1)}>
                    &raquo;
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </>
      )}

      {selectedCalculationId && (
        <CorrelationModal
          calculationId={selectedCalculationId}
          onClose={() => setSelectedCalculationId(null)}
        />
      )}
    </div>
  );
};
